/**
 * 配信詳細の「反応の内訳」カード（設計書 9.1 / html 図1）。
 *
 * 設計書: `docs/design-docs/deliveries/open-click-analytics.md`。
 * 本文中の `設計書 N.M` はこの md、`html N.M` / `html 図N` は同ディレクトリの
 * `open-click-analytics.html` を指す。
 */
import { LegendItem } from "components/LegendItem";
import { TabButton } from "components/TabButton";
import {
  AXIS_WIDTH,
  BREAKDOWN_BAND_COLORS,
  BREAKDOWN_BAND_TEXT_COLORS,
  CHART_AXIS_LABEL,
  CHART_AXIS_LABEL_FONT_SIZE,
  CHART_AXIS_LINE,
  CHART_AXIS_TICK_RATIOS,
  CHART_GRID_LINE,
  CHART_GRID_RATIOS,
  CHART_LABEL,
  CHART_TICK_LABEL_BASELINE_OFFSET,
  DISCLOSURE_COLOR,
  REACTION_SCALE,
} from "constants/delivery-analytics";
import { LOCAL_STORAGE_REACTION_BREAKDOWN_KEY } from "constants/local-storage-keys";
import type {
  DeliveryAnalytics,
  DeviceBreakdownItem,
  LinkBreakdownItem,
  TimelinePoint,
} from "hooks/use-delivery-api";
import { cn } from "lib/utils";
import { type FC, type ReactNode, useState } from "react";
import {
  formatCount,
  largestRemainderPercents,
} from "utils/delivery-analytics";

/** 時系列グラフの 3 系列 */
type TimelineSeriesKey = "opens" | "clicks" | "replies";

const TIMELINE_SERIES: ReadonlyArray<{
  key: TimelineSeriesKey;
  label: string;
  color: string;
}> = [
  { key: "opens", label: "開封", color: REACTION_SCALE[2].color },
  { key: "clicks", label: "クリック", color: REACTION_SCALE[5].color },
  { key: "replies", label: "返信", color: REACTION_SCALE[6].color },
];

/** デバイス区分の表示名（帯の中 / 凡例） */
const DEVICE_LABELS: ReadonlyArray<{
  deviceType: string;
  short: string;
  full: string;
}> = [
  { deviceType: "mobile", short: "スマホ", full: "スマートフォン" },
  { deviceType: "desktop", short: "PC", full: "PC" },
  { deviceType: "unknown", short: "不明", full: "不明" },
];

/**
 * 折れ線グラフの描画枠（viewBox 0 0 640 150 の内側）。
 * 左端は y 軸帯の幅そのものなので、グラフ共通の `AXIS_WIDTH` を使う。
 */
const CHART_RIGHT = 620;
const CHART_TOP = 14;
const CHART_BOTTOM = 118;
/** 横軸ラベルのベースライン */
const X_LABEL_Y = 133;

/** 開閉状態を覚えるアコーディオン 3 本の識別子（localStorage のキー内で使う） */
type AccordionKey = "links" | "devices" | "timeline";

type ReactionBreakdownCardProps = {
  analytics: DeliveryAnalytics;
};

/**
 * 配信詳細の「反応の内訳」カード（アコーディオン 3 本。設計書 9.1 / html 図1）。
 * 初回はすべて閉じ、以降は 3 本それぞれ前回の開閉状態を復元する。
 * 実データの総量が一言で言えるものだけ要約値を出す。
 * @param {ReactionBreakdownCardProps} props 分析データ
 * @return {JSX.Element} カード
 */
export const ReactionBreakdownCard: FC<ReactionBreakdownCardProps> = ({
  analytics,
}) => {
  const totalClicks = analytics.linkBreakdown.reduce(
    (sum, link) => sum + link.totalClicks,
    0,
  );
  const devicePercents = largestRemainderPercents(
    DEVICE_LABELS.map(d =>
      recipientsOf(analytics.deviceBreakdown, d.deviceType),
    ),
  );
  const mobilePercent = devicePercents[0];
  const hasDevice = analytics.deviceBreakdown.some(d => d.recipients > 0);

  return (
    <div className="card bg-base-100 shadow-md mb-4">
      <div className="card-body">
        <h3 className="card-title text-base">反応の内訳</h3>

        <Accordion
          storageKey="links"
          title="リンク別クリック内訳"
          summary={`${analytics.linkBreakdown.length} リンク・${totalClicks} 回`}
        >
          <LinkBreakdownTable items={analytics.linkBreakdown} />
        </Accordion>

        <Accordion
          storageKey="devices"
          title="読まれているデバイス"
          summary={hasDevice ? `スマホ ${mobilePercent}%` : undefined}
        >
          <DeviceBreakdownBar
            items={analytics.deviceBreakdown}
            percents={devicePercents}
            hasDevice={hasDevice}
          />
        </Accordion>

        <Accordion storageKey="timeline" title="時系列（送信からの経過時間）">
          <TimelineChart points={analytics.timeline} />
        </Accordion>
      </div>
    </div>
  );
};

type AccordionProps = {
  /** 開閉状態を覚える識別子 */
  storageKey: AccordionKey;
  title: string;
  summary?: string;
  children: ReactNode;
};

/**
 * 開閉の三角を持つアコーディオン 1 本（閉＝右向き / 開＝下向き）。
 * 開閉状態は 3 本それぞれ独立に localStorage へ覚え、次に開いたとき復元する。
 * @param {AccordionProps} props 識別子・見出し・要約値・中身
 * @return {JSX.Element} アコーディオン
 */
const Accordion: FC<AccordionProps> = ({
  storageKey,
  title,
  summary,
  children,
}) => {
  const [open, setOpen] = useState(() => readAccordionOpen(storageKey));

  const toggle = (): void => {
    const next = !open;
    setOpen(next);
    writeAccordionOpen(storageKey, next);
  };

  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-white">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left"
      >
        <svg
          viewBox="0 0 8 8"
          width={8}
          height={8}
          fill={open ? DISCLOSURE_COLOR.open : DISCLOSURE_COLOR.closed}
          aria-hidden
          className="shrink-0"
        >
          <title>{open ? "閉じる" : "開く"}</title>
          {open ? <path d="M0 2 L8 2 L4 7 Z" /> : <path d="M2 0 L7 4 L2 8 Z" />}
        </svg>
        <span className={cn(open ? "font-semibold" : "font-medium")}>
          {title}
        </span>
        {summary && (
          <span className="text-xs text-base-content/60">{summary}</span>
        )}
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
};

type LinkBreakdownTableProps = {
  items: LinkBreakdownItem[];
};

/**
 * リンク別クリック内訳の表（クリック 0 回のリンクも行として出す）。
 * URL は何も畳まずそのまま出し、長いものはセル内で折り返す（横スクロールに逃がさない）。
 * @param {LinkBreakdownTableProps} props リンク一覧
 * @return {JSX.Element} 表
 */
const LinkBreakdownTable: FC<LinkBreakdownTableProps> = ({ items }) => {
  if (items.length === 0) {
    return (
      <p className="text-xs text-base-content/60">本文にリンクがありません</p>
    );
  }
  return (
    <table className="table table-xs">
      <thead>
        <tr>
          <th>リンク</th>
          <th className="text-right">総クリック（回）</th>
          <th className="text-right">クリックした宛先数</th>
        </tr>
      </thead>
      <tbody>
        {items.map(link => (
          <tr key={link.linkId}>
            <td className="[overflow-wrap:anywhere] whitespace-normal">
              {link.url}
            </td>
            <td className="text-right">{formatCount(link.totalClicks)}</td>
            <td className="text-right">{formatCount(link.uniqueClicks)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

type DeviceBreakdownBarProps = {
  items: DeviceBreakdownItem[];
  percents: number[];
  hasDevice: boolean;
};

/**
 * デバイス別開封の横帯（反応スケールの外なのでインディゴ 3 段）。
 * @param {DeviceBreakdownBarProps} props 内訳と帯の幅
 * @return {JSX.Element} 横帯と凡例
 */
const DeviceBreakdownBar: FC<DeviceBreakdownBarProps> = ({
  items,
  percents,
  hasDevice,
}) => {
  if (!hasDevice) {
    return <p className="text-xs text-base-content/60">開封がまだありません</p>;
  }
  return (
    <>
      <div className="flex h-6 overflow-hidden rounded-md">
        {DEVICE_LABELS.map((device, index) => {
          const width = percents[index];
          if (width <= 0) return null;
          return (
            <span
              key={device.deviceType}
              className="flex items-center justify-center text-[11px] font-bold"
              style={{
                width: `${width}%`,
                background: BREAKDOWN_BAND_COLORS[index],
                color: BREAKDOWN_BAND_TEXT_COLORS[index],
              }}
            >
              {width >= 12 ? `${device.short} ${width}%` : device.short}
            </span>
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-600">
        {DEVICE_LABELS.map((device, index) => (
          <LegendItem
            key={device.deviceType}
            color={BREAKDOWN_BAND_COLORS[index]}
            label={`${device.full} ${recipientsOf(items, device.deviceType)} 宛先`}
          />
        ))}
      </div>
    </>
  );
};

type TimelineChartProps = {
  points: TimelinePoint[];
};

/**
 * 送信からの経過時間ごとの折れ線グラフ（固定 28 点）。
 * @param {TimelineChartProps} props 時系列
 * @return {JSX.Element} グラフ
 */
const TimelineChart: FC<TimelineChartProps> = ({ points }) => {
  const [active, setActive] = useState<ReadonlySet<TimelineSeriesKey>>(
    new Set<TimelineSeriesKey>(["opens", "clicks"]),
  );

  if (points.length === 0) {
    return <p className="text-xs text-base-content/60">データがありません</p>;
  }

  const toggle = (key: TimelineSeriesKey): void => {
    setActive(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const rawMax = Math.max(
    1,
    ...points.flatMap(p =>
      TIMELINE_SERIES.filter(s => active.has(s.key)).map(s => p[s.key]),
    ),
  );
  // 目盛りが整数になるよう 4 の倍数に切り上げる（0 / 1/4 / 1/2 / 3/4 / max の 5 本）
  const max = Math.max(4, Math.ceil(rawMax / 4) * 4);
  const step = (CHART_RIGHT - AXIS_WIDTH) / Math.max(1, points.length - 1);

  /**
   * 件数を y 座標に変換する（罫線・目盛・折れ線がすべてこれを通る）
   * @param {number} value 件数
   * @return {number} y 座標
   */
  const toY = (value: number): number =>
    CHART_BOTTOM - ((CHART_BOTTOM - CHART_TOP) * value) / max;

  return (
    <>
      <div className="mb-2 flex gap-4">
        {TIMELINE_SERIES.map(series => (
          <TabButton
            key={series.key}
            active={active.has(series.key)}
            onClick={() => toggle(series.key)}
            // 系列は独立に on/off できるトグルなので、タブではなく押した状態を持つ
            aria-pressed={active.has(series.key)}
          >
            {series.label}
          </TabButton>
        ))}
      </div>
      <div className="rounded-lg border border-slate-200 p-2">
        <svg
          viewBox="0 0 640 150"
          className="block h-auto w-full"
          role="img"
          aria-label="送信からの経過時間ごとの開封・クリック・返信の折れ線グラフ"
        >
          <title>送信からの経過時間ごとの反応</title>
          <line
            x1={AXIS_WIDTH}
            y1={CHART_BOTTOM}
            x2={CHART_RIGHT}
            y2={CHART_BOTTOM}
            stroke={CHART_AXIS_LINE}
            strokeWidth={1}
          />
          <line
            x1={AXIS_WIDTH}
            y1={CHART_TOP}
            x2={AXIS_WIDTH}
            y2={CHART_BOTTOM}
            stroke={CHART_AXIS_LINE}
            strokeWidth={1}
          />
          <g stroke={CHART_GRID_LINE} strokeWidth={1}>
            {CHART_GRID_RATIOS.map(ratio => (
              <line
                key={ratio}
                x1={AXIS_WIDTH}
                y1={toY(max * ratio)}
                x2={CHART_RIGHT}
                y2={toY(max * ratio)}
              />
            ))}
          </g>
          <g
            fill={CHART_AXIS_LABEL}
            fontSize={CHART_AXIS_LABEL_FONT_SIZE}
            fontFamily="sans-serif"
          >
            {CHART_AXIS_TICK_RATIOS.map(ratio => (
              <text
                key={ratio}
                x={12}
                y={toY(max * ratio) + CHART_TICK_LABEL_BASELINE_OFFSET}
              >
                {Math.round(max * ratio)}
              </text>
            ))}
            <text x={20} y={toY(0) + CHART_TICK_LABEL_BASELINE_OFFSET}>
              0
            </text>
            <text x={46} y={10}>
              件数
            </text>
            <text x={490} y={147}>
              送信からの経過時間
            </text>
          </g>
          {/* 横軸の項目ラベルは y 目盛より濃い CHART_LABEL（トークンの定義どおり） */}
          <g
            fill={CHART_LABEL}
            fontSize={CHART_AXIS_LABEL_FONT_SIZE}
            fontFamily="sans-serif"
          >
            <text x={48} y={X_LABEL_Y}>
              0h
            </text>
            {points.map((point, index) =>
              point.tick ? (
                <text
                  key={point.index}
                  x={AXIS_WIDTH + step * index - 6}
                  y={X_LABEL_Y}
                >
                  {point.label}
                </text>
              ) : null,
            )}
          </g>
          {TIMELINE_SERIES.filter(series => active.has(series.key)).map(
            series => (
              <polyline
                key={series.key}
                fill="none"
                stroke={series.color}
                strokeWidth={2.5}
                points={points
                  .map((point, index) => {
                    const x = AXIS_WIDTH + step * index;
                    return `${x.toFixed(1)},${toY(point[series.key]).toFixed(1)}`;
                  })
                  .join(" ")}
              />
            ),
          )}
        </svg>
      </div>
    </>
  );
};

/**
 * デバイス区分の開封宛先数を引く。
 * @param {ReadonlyArray<DeviceBreakdownItem>} items デバイス別内訳
 * @param {string} deviceType デバイス区分
 * @return {number} 開封した宛先数
 */
function recipientsOf(
  items: ReadonlyArray<DeviceBreakdownItem>,
  deviceType: string,
): number {
  return items.find(item => item.deviceType === deviceType)?.recipients ?? 0;
}

/**
 * 覚えてある開閉状態を localStorage から読む（3 本ぶんが 1 つの JSON に入る）。
 *
 * 読み書きの両方がこの「JSON を読んで object に絞り込む」処理を必要とするので
 * 1 本にまとめる。`try / catch` はプライベートウィンドウ等で
 * `localStorage` そのものが例外を投げるための意図的な防御なので残す。
 * @return {Record<string, unknown>} 覚えてある状態。読めなければ空
 */
function readAccordionState(): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_REACTION_BREAKDOWN_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    // 読めない環境では何も覚えていない扱いにする
    return {};
  }
}

/**
 * アコーディオン 1 本の開閉状態を localStorage から読む。
 * 読めない環境では既定（閉）で始める。
 * @param {AccordionKey} key アコーディオンの識別子
 * @return {boolean} 開いていたか
 */
function readAccordionOpen(key: AccordionKey): boolean {
  return readAccordionState()[key] === true;
}

/**
 * アコーディオン 1 本の開閉状態を localStorage に書く（他の 2 本は保つ）。
 * @param {AccordionKey} key アコーディオンの識別子
 * @param {boolean} open 開いたか
 * @return {void}
 */
function writeAccordionOpen(key: AccordionKey, open: boolean): void {
  const base = readAccordionState();
  try {
    localStorage.setItem(
      LOCAL_STORAGE_REACTION_BREAKDOWN_KEY,
      JSON.stringify({ ...base, [key]: open }),
    );
  } catch {
    // 保存できない環境では記憶しない（表示には影響させない）
  }
}
